import { useEffect } from "react";
import { useSelector } from "react-redux";

/**
 * Hook to get the current dcPlayerId (per-browser game identity).
 * 
 * Preferentially reads from Redux (authSlice.user.Id), falls back to localStorage.
 * Logs a warning if Redux and localStorage disagree (indicates potential auth flow issue).
 * 
 * @returns {{ dcPlayerId: string | null, isReady: boolean, mismatch: boolean, source: "redux" | "localStorage" | "none" }}
 */
export function useDcPlayerId() {
  // Read from Redux auth slice
  const reduxId = useSelector((state) => state.auth?.user?.Id) || null;

  // Safely read from localStorage (guard for SSR)
  let localId = null;
  if (typeof window !== "undefined") {
    localId = localStorage.getItem("dcPlayerId") || null;
  }

  // Determine the effective dcPlayerId
  const dcPlayerId = reduxId || localId || null;

  // Compute status flags
  const isReady = Boolean(dcPlayerId);
  const mismatch = Boolean(reduxId && localId && reduxId !== localId);

  // Determine source for debugging
  let source = "none";
  if (reduxId) {
    source = "redux";
  } else if (localId) {
    source = "localStorage";
  }

  // Log mismatch as error (one-time per mismatch state change)
  useEffect(() => {
    if (mismatch) {
      console.error(
        "[useDcPlayerId] dcPlayerId mismatch: Redux user.Id !== localStorage.dcPlayerId",
        { reduxId, localId }
      );
    }
  }, [mismatch, reduxId, localId]);

  return { dcPlayerId, isReady, mismatch, source };
}
